import { Link } from "react-router-dom";
import type { Currency } from "../entities/ledger/types";
import { AccountsPanel } from "../features/accounts/AccountsPanel";
import { planningMonth } from "../features/planning/api";
import { BudgetHeroCard } from "../features/planning/BudgetHeroCard";
import { usePlanning } from "../features/planning/usePlanning";
import { ActivityPanel } from "../features/movement/ActivityPanel";
import { useDashboard } from "../features/workspace/useDashboard";
import { type ApiExchange } from "../shared/api/client";
import { greeting, money } from "../shared/lib/format";
import { Disclosure } from "../shared/ui/Disclosure";
import { ErrorPanel, LoadingPanel } from "../shared/ui/PageState";

interface DashboardPageProps {
  currency: Currency;
  exchange: ApiExchange | null;
  onNotify: (message: string) => void;
}

const OVERVIEW_ENTRIES = 6;

// The overview answers "where do I stand" and nothing else. Recording,
// statements, category breakdowns and corrections each have their own page
// under /journal; this screen only links to them.
export function DashboardPage({ currency, exchange, onNotify }: DashboardPageProps) {
  const dashboard = useDashboard(currency);
  // The headline figure is this month's budget, which lives in planning.
  const planning = usePlanning(dashboard.data?.ids.book, planningMonth(0));

  const data = dashboard.data;
  const failed = dashboard.isError || (!dashboard.isPending && !data);
  if (!data) {
    return (
      <section className="dashboard view" aria-labelledby="dashboard-title">
        <div className="page-heading"><div><p className="eyebrow">Ledgerly</p><h1 id="dashboard-title">{greeting()}</h1></div></div>
        {dashboard.isPending ? <LoadingPanel message="Loading your books…" /> : null}
        {failed ? <ErrorPanel error={dashboard.error} onRetry={() => dashboard.refetch()} what="your books" scope="collection" /> : null}
      </section>
    );
  }
  const flow = data.moneyFlow;
  const baseCurrency = data.ids.baseCurrency;

  return (
    <section className="dashboard view" aria-labelledby="dashboard-title">
      <div className="page-heading">
        <div>
          <p className="eyebrow">{new Intl.DateTimeFormat("en", { month: "long", year: "numeric" }).format(new Date(data.ids.period.start))}</p>
          <h1 id="dashboard-title">{greeting()}</h1>
        </div>
        <div className="heading-actions">
          <button className="icon-button bordered" type="button" onClick={() => dashboard.refetch()} disabled={dashboard.isFetching}
            aria-label={dashboard.isFetching ? "Refreshing this book" : "Reload this book"} title={dashboard.isFetching ? "Refreshing…" : "Reload this book"}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 7v5h-5M4 17v-5h5M18.5 9A7 7 0 0 0 6.2 6.2L4 8m2 7a7 7 0 0 0 12.3 2.8L20 16" /></svg>
          </button>
          <Link className="primary-button compact" to="/journal/record"><span>Record money</span><span aria-hidden="true">→</span></Link>
        </div>
      </div>

      <div className="summary-grid">
        <BudgetHeroCard summary={planning.data?.summary} loading={planning.isPending} />

        <article className="panel balance-card flow-card">
          <div className="card-label">
            <span>Money flow · every account</span>
            {flow?.classificationComplete === false
              ? <span className="account-chip warning">Incomplete</span>
              : null}
          </div>
          <div className="balance-amount">{flow ? money(flow.netChange.minor, flow.netChange.currency, true) : money(0, baseCurrency)}</div>
          <p>Net change across your asset accounts this month. This counts every movement, not only the categories a budget watches.</p>
          <dl className="health-list">
            <div><dt>All money in</dt><dd>{flow ? money(flow.knownExternalInflow.minor, flow.knownExternalInflow.currency) : "—"}</dd></div>
            <div><dt>All money out</dt><dd>{flow ? money(flow.knownExternalOutflow.minor, flow.knownExternalOutflow.currency) : "—"}</dd></div>
            <div><dt>Moved between your accounts</dt><dd>{flow ? money(flow.internalTransferBookValue.minor, flow.internalTransferBookValue.currency) : "—"}</dd></div>
          </dl>
          <Link className="panel-link" to="/journal/categories">Where the money went</Link>
        </article>
      </div>

      <AccountsPanel accounts={data.accounts} balances={data.balances} reportingCurrency={data.report.reportingCurrency} limit={6} />

      <ActivityPanel bookID={data.ids.book} entries={data.entries} accounts={data.accounts} onNotify={onNotify} categories={data.categories} preview={OVERVIEW_ENTRIES} />

      <Disclosure title="API inspector" hint="Last request and response">
        <pre className="code-block">{exchange ? JSON.stringify(exchange, null, 2) : "No requests yet."}</pre>
      </Disclosure>
    </section>
  );
}
